import { GridSortModel } from '@mui/x-data-grid'
import {
  createSelectorHooks,
  ZustandHookSelectors
} from 'auto-zustand-selectors-hook'
import create from 'zustand'
import { numberToQuantityString } from 'utils'

const normalizeProductQuantities = (product: any) => ({
  ...product,
  quantity: numberToQuantityString(product.quantity),
  quantityUn: numberToQuantityString(product.quantityUn)
})

type RegisterSequence = {
  id: string
  itemSequence: number
}

interface Order {
  form: any
  paymentStep: any
  currentStep: string
  currentOrderId: string
  orderMode: 'create' | 'view' | 'edit'
  productMode: 'create' | 'view' | 'edit'
  selectedItem: any
  orderListTotal: any
  search: string
  orderFilters: any
  orderProductListFilters: any
  isEditItemOpen: boolean
  opportunity: any
  sequence: any
  filters: any
  isTaxesRecalculePending: boolean
  isRecalculatingDuplicateOrder: boolean
  sort: GridSortModel
  filtersForm: any
  setIsTaxesRecalculePending: (isTaxesRecalculePending: boolean) => void
  setIsRecalculatingDuplicateOrder: (
    isRecalculatingDuplicateOrder: boolean
  ) => void
  setSearchParamOpportunityId: (opportunity: any) => void
  setIsEditItemOpen: (isEditItemOpen: boolean) => void
  setSearch: (search: string) => void
  setCurrentOrderId: (orderId: string) => void
  setForm: (data: any) => void
  setCurrentStep: (step: string) => void
  addProductForm: (data: any) => void
  addProductsForm: (data: any[]) => void
  removeFormItem: (id: string) => void
  resetForm: () => void
  setOrderMode: (orderMode: 'create' | 'view' | 'edit') => void
  setProductMode: (productMode: 'create' | 'view' | 'edit') => void
  setSelectedItem: (item: any) => void
  setOrderFilters: (data: any) => void
  setOrderProductListFilters: (data: any) => void
  resetOrderFilters: (data: any) => void
  setOrderListTotal: (data: any) => void
  nextSequence: (id: string) => void
  registerSequence: (data: RegisterSequence) => void
  setSequence: (data: any) => void
  setFilters: (filters: any) => void
  setFiltersForm: (filters: any) => void
  setSort: (filters: any) => void
}

const useStoreBase = create<Order>()((set) => ({
  form: {
    orderItems: [],
    installments: []
  },
  paymentStep: {},
  currentStep: '',
  currentOrderId: '',
  orderMode: 'view',
  productMode: 'view',
  selectedItem: {},
  orderListTotal: 0,
  search: '',
  orderFilters: {},
  orderProductListFilters: {},
  isEditItemOpen: false,
  opportunity: {},
  sequence: {},
  filtersForm: {},
  filters: { listMode: 'proposals' },
  isTaxesRecalculePending: false,
  isRecalculatingDuplicateOrder: false,
  sort: [
    {
      field: 'proposalId',
      sort: 'desc'
    }
  ],
  setIsTaxesRecalculePending: (isTaxesRecalculePending: boolean) =>
    set({ isTaxesRecalculePending }),
  setIsRecalculatingDuplicateOrder: (isRecalculatingDuplicateOrder: boolean) =>
    set({ isRecalculatingDuplicateOrder }),
  setIsEditItemOpen: (isEditItemOpen: boolean) => set({ isEditItemOpen }),
  setForm: (data: any) =>
    set((state: any) => ({ form: { ...state.form, ...data } })),
  setOrderFilters: (orderFilters) => set({ orderFilters }),
  setOrderProductListFilters: (orderProductListFilters) =>
    set({ orderProductListFilters }),
  resetOrderFilters: () => set({ orderFilters: {} }),
  setSelectedItem: (selectedItem) => set({ selectedItem }),
  setOrderMode: (orderMode) => set({ orderMode }),
  setProductMode: (productMode) => set({ productMode }),
  setCurrentOrderId: (currentOrderId: any) => set({ currentOrderId }),
  setOrderListTotal: (orderListTotal) => set({ orderListTotal }),
  setSearch: (search) => set({ search }),
  setCurrentStep: (step: string) => set(() => ({ currentStep: step })),
  setFilters: (filters: any) => set({ filters }),
  setFiltersForm: (filtersForm: any) => set({ filtersForm }),
  setSort: (sort: any) => set({ sort }),
  addProductsForm: (products: any) =>
    set((state: any) => {
      return {
        form: {
          ...state.form,
          orderItems: products.map(normalizeProductQuantities)
        }
      }
    }),
  addProductForm: (product: any) =>
    set((state: any) => {
      const normalizedProduct = normalizeProductQuantities(product)
      const isEmpty = !state?.form?.orderItems?.length
      let orderItems = [normalizedProduct]

      if (!isEmpty) {
        const existsIndex = state?.form?.orderItems.findIndex(
          (ele: any) => ele.uuid === normalizedProduct.uuid
        )

        if (existsIndex > -1) {
          orderItems = state.form.orderItems.map((item: any, index: number) =>
            index === existsIndex ? normalizedProduct : item
          )
        } else {
          orderItems = state.form.orderItems.concat(normalizedProduct)
        }
      }

      return {
        form: {
          ...state.form,
          orderItems
        }
      }
    }),
  removeFormItem: (uuid: any) =>
    set((state: any) => {
      const orderItems = state.form.orderItems.filter(
        (item: any) => item.uuid !== uuid
      )

      return {
        form: {
          ...state.form,
          orderItems
        }
      }
    }),
  nextSequence: (id: string) =>
    set((state: Order) => {
      let sequence = { ...state.sequence }

      const hasKey = id in state?.sequence

      const arr: number[] = Object.values(sequence)

      const maxSequence = Math.max(...arr)

      if (!hasKey && maxSequence) sequence[id] = maxSequence + 1

      if (maxSequence < 0) sequence = { [id]: 1 }

      return {
        sequence
      }
    }),
  registerSequence: ({ id, itemSequence }: RegisterSequence) =>
    set((state: Order) => {
      const sequence = { ...state.sequence }
      sequence[id] = itemSequence
      return {
        sequence
      }
    }),
  setSequence: (sequence: any) =>
    set((state: any) => ({ sequence: { ...state.sequence, ...sequence } })),
  resetForm: () =>
    set({
      form: {
        orderItems: [],
        installments: [],
        orderId: undefined
      },
      sequence: {},
      currentStep: ''
    }),
  setSearchParamOpportunityId: (opportunity: {
    id: string
    opportunityDate: string
  }) => set(() => ({ opportunity }))
}))

export const useOrderStore = createSelectorHooks(
  useStoreBase
) as typeof useStoreBase & ZustandHookSelectors<Order>
